import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { DateRange } from 'react-date-range'
import { format } from 'date-fns'
import { getSiteSettings } from '../utils/siteSettings'
import { formatDateOnly } from '../utils/flightDateTime'
import { buildFlightPath } from '../utils/flightUrl'
import { LITEAPI_PROXY_URL } from '../config/api'
import { useAirportSearch } from '../hooks/useAirportSearch'
import { FiCalendar, FiGift, FiKey, FiUser, FiMapPin, FiSearch, FiX, FiChevronRight, FiArrowRight, FiGlobe } from 'react-icons/fi'
import { HiOutlineBuildingOffice2, HiOutlineHomeModern, HiOutlineUsers } from 'react-icons/hi2'
import { IoAirplaneOutline } from 'react-icons/io5'
import { FaHiking } from 'react-icons/fa'
import { BiBed } from 'react-icons/bi'
import CalendarPicker from '../components/CalendarPicker'
import RegionalDeals from '../components/RegionalDeals'
import PopularDestinations from '../components/PopularDestinations'
import FeaturedMarriottHawaii from '../components/FeaturedMarriottHawaii'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import './HomePage.css'

const PLACES_API = `${LITEAPI_PROXY_URL}/api/data/places`
const FLIGHTS_SEARCH_ENABLED = true
const isFlightCityCode = (airport = {}) => airport.isCityCode || !airport.icao || /all airports/i.test(airport.name || '')
const flightAirportName = (airport = {}) =>
  isFlightCityCode(airport) ? `${airport.city || airport.name} - all airports` : airport.name
const flightAirportMeta = (airport = {}) => {
  const location = [airport.city, airport.state, airport.country].filter(Boolean).join(', ')
  return `${location}${location ? ' - ' : ''}${isFlightCityCode(airport) ? 'city code for nearby airports' : airport.icao}`
}
const CABIN_CLASS_OPTIONS = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium_economy', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First Class' },
]

function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Search state
  const [destination, setDestination] = useState('')
  const [selectedPlace, setSelectedPlace] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const searchTimeout = useRef(null)
  const abortControllerRef = useRef(null)
  const lastQueryRef = useRef('') // Track last query to prevent duplicates
  const lastRequestTime = useRef(0) // Track last request time for rate limiting
  const searchCache = useRef({}) // Cache search results to avoid repeated API calls
  const destinationRef = useRef(null)

  // Mobile modal states
  const [showDestinationModal, setShowDestinationModal] = useState(false)
  const [showDateModal, setShowDateModal] = useState(false)
  const [showGuestModal, setShowGuestModal] = useState(false)
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const mobileSearchInputRef = useRef(null)

  // Date state
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dateRange, setDateRange] = useState([{ startDate: null, endDate: null, key: 'selection' }])
  const dateRef = useRef(null)

  // Guest state
  const [showGuestPicker, setShowGuestPicker] = useState(false)
  const [rooms, setRooms] = useState([{ adults: 2, children: 0, childAges: [] }])
  const guestRef = useRef(null)

  // Search type toggle: 'hotels', 'flights', 'rentals', 'experiences'
  const [searchType, setSearchType] = useState('hotels')
  const [viatorDestUrl, setViatorDestUrl] = useState(null)

  // Airport autocomplete (one hook instance per field)
  const fromAirport = useAirportSearch()
  const toAirport = useAirportSearch()
  const flightFromRef = useRef(null)
  const flightToRef = useRef(null)

  // Mobile airport modal: null | 'from' | 'to'
  const [airportModalField, setAirportModalField] = useState(null)
  const airportModalInputRef = useRef(null)

  // Click-outside to close airport dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (flightFromRef.current && !flightFromRef.current.contains(e.target)) fromAirport.setShowSuggestions(false)
      if (flightToRef.current && !flightToRef.current.contains(e.target)) toAirport.setShowSuggestions(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [fromAirport.setShowSuggestions, toAirport.setShowSuggestions])

  // Focus airport modal input when it opens
  useEffect(() => {
    if (airportModalField && airportModalInputRef.current) {
      setTimeout(() => airportModalInputRef.current?.focus(), 100)
    }
  }, [airportModalField])

  // Active airport hook for the open modal field
  const activeAirport = airportModalField === 'from' ? fromAirport : toAirport

  // Flights state
  const [flightAdults, setFlightAdults] = useState(1)
  const [flightChildAges, setFlightChildAges] = useState([]) // one entry per child, value = age (2-11)
  const [flightInfants, setFlightInfants] = useState(0)
  const [showTravelerPicker, setShowTravelerPicker] = useState(false)
  const travelerRef = useRef(null)
  const cabinRef = useRef(null)
  const [flightTripType, setFlightTripType] = useState('roundtrip')
  const [flightCabinClass, setFlightCabinClass] = useState('economy')
  const [showCabinPicker, setShowCabinPicker] = useState(false)

  const flightChildren = flightChildAges.length
  const flightCabinLabel = CABIN_CLASS_OPTIONS.find(option => option.value === flightCabinClass)?.label || 'Economy'

  const addFlightChild = () => setFlightChildAges(prev => [...prev, 8])
  const removeFlightChild = () => setFlightChildAges(prev => prev.slice(0, -1))
  const setFlightChildAge = (idx, age) => setFlightChildAges(prev => prev.map((a, i) => i === idx ? age : a))

  const flightTravelerSummary = () => {
    if (flightChildren === 0 && flightInfants === 0) return `${flightAdults} Adult${flightAdults !== 1 ? 's' : ''}`
    const parts = [`${flightAdults} Adult${flightAdults !== 1 ? 's' : ''}`]
    if (flightChildren > 0) parts.push(`${flightChildren} Child${flightChildren !== 1 ? 'ren' : ''}`)
    if (flightInfants > 0) parts.push(`${flightInfants} Infant${flightInfants !== 1 ? 's' : ''}`)
    return parts.join(', ')
  }

  // Focus mobile search input when modal opens
  useEffect(() => {
    if (showDestinationModal && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 100)
    }
  }, [showDestinationModal])

  // Clear suggestions when switching search type
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    setSuggestions([])
    setShowSuggestions(false)
    lastQueryRef.current = ''
    setViatorDestUrl(null)
  }, [searchType])

  // Fetch places with rate limiting, caching, and abort controller
  const fetchPlaces = async (query) => {
    // Skip if query is too short
    if (query.length < 3) return setSuggestions([])
    
    // Normalize query for caching (lowercase, trimmed)
    const cacheKey = query.toLowerCase().trim()
    
    // Check cache first - return cached results immediately
    if (searchCache.current[cacheKey]) {
      console.log('Using cached results for:', cacheKey)
      setSuggestions(searchCache.current[cacheKey])
      lastQueryRef.current = query
      return
    }
    
    // Skip if same as last query (already fetching or fetched)
    if (query === lastQueryRef.current) return
    
    // Enforce minimum 2 seconds between requests to avoid rate limiting
    const now = Date.now()
    const timeSinceLastRequest = now - lastRequestTime.current
    if (timeSinceLastRequest < 2000) {
      // Schedule for later instead of making request now
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      searchTimeout.current = setTimeout(() => fetchPlaces(query), 2000 - timeSinceLastRequest + 100)
      return
    }
    
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    // Update tracking refs
    lastQueryRef.current = query
    lastRequestTime.current = now
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController()
    
    setLoadingSuggestions(true)
    try {
      const resp = await fetch(
        `${PLACES_API}?textQuery=${encodeURIComponent(query)}&language=en`,
        { signal: abortControllerRef.current.signal }
      )
      if (!resp.ok) {
        // Handle rate limit specifically
        if (resp.status === 429) {
          console.warn('Rate limited, will retry in 3 seconds')
          // Retry after 3 seconds
          setTimeout(() => {
            lastRequestTime.current = 0 // Reset to allow retry
            fetchPlaces(query)
          }, 3000)
          return
        }
        throw new Error('API error')
      }
      const data = await resp.json()
      const places = (data.data || data.places || []).slice(0, 6)
      console.log('Places API response:', places)
      
      // Cache the results
      const cacheKey = query.toLowerCase().trim()
      searchCache.current[cacheKey] = places
      
      setSuggestions(places)
    } catch (e) {
      // Only log if it's not an abort error
      if (e.name !== 'AbortError') {
        console.warn('Places API error:', e)
      }
    }
    setLoadingSuggestions(false)
  }

  // Fetch Viator destinations for Experiences autocomplete
  const fetchViatorDestinations = async (query) => {
    if (query.length < 3) return setSuggestions([])
    const cacheKey = `viator_${query.toLowerCase().trim()}`
    if (searchCache.current[cacheKey]) {
      setSuggestions(searchCache.current[cacheKey])
      lastQueryRef.current = query
      return
    }
    if (query === lastQueryRef.current) return
    if (abortControllerRef.current) abortControllerRef.current.abort()
    lastQueryRef.current = query
    abortControllerRef.current = new AbortController()
    setLoadingSuggestions(true)
    try {
      const resp = await fetch(
        `https://us-central1-voyabay-hotels.cloudfunctions.net/viatorDestinationSearch?q=${encodeURIComponent(query)}`,
        { signal: abortControllerRef.current.signal }
      )
      if (!resp.ok) throw new Error('API error')
      const data = await resp.json()
      const places = (data.destinations || []).map(d => ({
        displayName: d.name,
        formattedAddress: d.parentName || '',
        viatorUrl: d.url,
        viatorId: d.id,
        isViator: true,
      }))
      searchCache.current[cacheKey] = places
      setSuggestions(places)
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('Viator search error:', e)
    }
    setLoadingSuggestions(false)
  }

  const handleDestinationChange = (e) => {
    const text = e.target.value
    setDestination(text)
    setSelectedPlace(null)
    
    // Clear any pending timeout
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    
    if (text.length >= 3) {
      // Use 1 second debounce to reduce API calls
      const fetchFn = searchType === 'experiences' ? fetchViatorDestinations : fetchPlaces
      searchTimeout.current = setTimeout(() => fetchFn(text), 1000)
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
      lastQueryRef.current = '' // Reset last query
    }
  }

  const handleMobileSearchChange = (e) => {
    const text = e.target.value
    setMobileSearchQuery(text)
    
    // Clear any pending timeout
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    
    if (text.length >= 3) {
      // Use 1 second debounce to reduce API calls
      const fetchFn = searchType === 'experiences' ? fetchViatorDestinations : fetchPlaces
      searchTimeout.current = setTimeout(() => fetchFn(text), 1000)
    } else {
      setSuggestions([])
      lastQueryRef.current = '' // Reset last query
    }
  }

  const handleSelectPlace = (place) => {
    // Format display name with location (like mobile app)
    const name = place.displayName || place.name || ''
    const address = place.formattedAddress || ''
    const fullDisplay = address ? `${name}, ${address}` : name
    
    setDestination(fullDisplay)
    setMobileSearchQuery(fullDisplay)
    setSelectedPlace(place)
    setViatorDestUrl(place.viatorUrl || null)
    setSuggestions([])
    setShowSuggestions(false)
    setShowDestinationModal(false)
    // Blur the input to remove cursor
    if (document.activeElement) {
      document.activeElement.blur()
    }
  }

  // Close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target
      if (destinationRef.current && !destinationRef.current.contains(e.target)) setShowSuggestions(false)
      if (dateRef.current && !dateRef.current.contains(e.target)) setShowDatePicker(false)
      if (guestRef.current && !guestRef.current.contains(e.target)) setShowGuestPicker(false)
      if (travelerRef.current && !travelerRef.current.contains(target) && !target.closest?.('.mobile-flight-travelers-picker')) setShowTravelerPicker(false)
      if (cabinRef.current && !cabinRef.current.contains(target) && !target.closest?.('.mobile-flight-cabin-picker')) setShowCabinPicker(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Prevent body scroll when modal is open
  useEffect(() => {
    const anyModalOpen = showDestinationModal || showDateModal || showGuestModal
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [showDestinationModal, showDateModal, showGuestModal])

  // Guest helpers
  const totalGuests = rooms.reduce((sum, r) => sum + r.adults + r.children, 0)
  const totalRooms = rooms.length
  const updateRoom = (idx, field, value) => {
    setRooms(rooms => rooms.map((r, i) => {
      if (i !== idx) return r
      if (field === 'children') return { ...r, children: value, childAges: Array(value).fill(1) }
      return { ...r, [field]: value }
    }))
  }
  const updateChildAge = (roomIdx, childIdx, age) => {
    setRooms(rooms => rooms.map((r, i) => {
      if (i !== roomIdx) return r
      const newAges = [...r.childAges]
      newAges[childIdx] = age
      return { ...r, childAges: newAges }
    }))
  }

  // Format display
  const formatDateDisplay = () => {
    const isOneWay = searchType === 'flights' && flightTripType === 'oneway'
    if (isOneWay) {
      return dateRange[0].startDate ? format(dateRange[0].startDate, 'EEE, MMM d') : 'Add date'
    }
    if (dateRange[0].startDate && dateRange[0].endDate) {
      return `${format(dateRange[0].startDate, 'MMM d')} - ${format(dateRange[0].endDate, 'MMM d')}`
    }
    return 'Add dates'
  }

  const getNightsCount = () => {
    if (dateRange[0].startDate && dateRange[0].endDate) {
      const nights = Math.ceil((dateRange[0].endDate - dateRange[0].startDate) / (1000 * 60 * 60 * 24))
      return nights > 0 ? nights : 0
    }
    return 0
  }

  const guestSummary = () => `${totalRooms} Room${totalRooms > 1 ? 's' : ''}, ${totalGuests} Guest${totalGuests > 1 ? 's' : ''}`

  // Truncate text with ellipsis (like mobile app)
  const truncateText = (text, max = 40) => {
    if (!text) return ''
    if (text.length <= max) return text
    return text.slice(0, max - 3) + '...'
  }

  // Format location with state/country (like mobile app)
  const formatLocationDisplay = (place) => {
    if (!place) return ''
    const name = place.displayName || place.name || ''
    const address = place.formattedAddress || ''
    
    // For localities/cities, combine name with address
    const types = place.types || []
    const isLocality = types.some(t => t && (t.toLowerCase().includes('locality') || t.toLowerCase().includes('political')))
    
    if (isLocality && address) {
      return `${name}, ${address}`
    }
    return name
  }

  // Get secondary text (state, country) for dropdown
  const getLocationSubtext = (place) => {
    if (!place) return ''
    const address = place.formattedAddress || ''
    return truncateText(address, 50)
  }

  // Check if a place is a hotel or lodging (uses includes for compound types like resort_hotel, extended_stay_hotel, etc.)
  const isHotelOrLodging = (place) => {
    if (!place || !place.types) return false
    const types = place.types
    return types.some(t => {
      if (!t) return false
      const lower = t.toLowerCase()
      return lower.includes('hotel') || lower.includes('lodging') || lower === 'motel' || lower === 'resort' || lower.includes('bed_and_breakfast') || lower.includes('guest_house')
    })
  }

  // Fetch place details to get coordinates for hotel/lodging
  const fetchPlaceDetails = async (placeId) => {
    try {
      const url = `${PLACES_API}/${encodeURIComponent(placeId)}`
      const resp = await fetch(url)
      if (!resp.ok) throw new Error('API error ' + resp.status)
      const json = await resp.json()
      return json.data || json
    } catch (e) {
      console.warn('[HomePage] fetchPlaceDetails error:', e)
      return null
    }
  }

  // Search handler
  const handleSearch = async () => {
    // Flights: build Google Flights URL
    if (searchType === 'flights') {
      if (!FLIGHTS_SEARCH_ENABLED) {
        setSearchType('hotels')
        return
      }
      if (!fromAirport.iata || !toAirport.iata) {
        alert('Please select an airport for both From and To.')
        return
      }
      if (!dateRange[0].startDate) {
        alert('Please select a departure date.')
        return
      }
      const params = {
        origin: fromAirport.iata,
        destination: toAirport.iata,
        originCodeType: fromAirport.selectedAirport?.codeType || 'airport',
        destinationCodeType: toAirport.selectedAirport?.codeType || 'airport',
        originLabel: fromAirport.selectedAirport ? flightAirportName(fromAirport.selectedAirport) : fromAirport.query,
        destinationLabel: toAirport.selectedAirport ? flightAirportName(toAirport.selectedAirport) : toAirport.query,
        departureDate: formatDateOnly(dateRange[0].startDate),
        returnDate: flightTripType === 'roundtrip' && dateRange[0].endDate
          ? formatDateOnly(dateRange[0].endDate)
          : undefined,
        adults: flightAdults,
        childAges: flightChildAges,
        infants: flightInfants,
        cabinClass: flightCabinClass,
        currency: 'USD',
        tripType: flightTripType,
      }
      navigate(buildFlightPath(params), { state: { params } })
      return
    }

    // Experiences: open Viator destination URL directly from autocomplete selection
    if (searchType === 'experiences') {
      const url = viatorDestUrl || 'https://experiences.voyabay.com'
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }

    const placeId = selectedPlace?.placeId || selectedPlace?.id || ''
    const checkin = dateRange[0].startDate ? format(dateRange[0].startDate, 'yyyy-MM-dd') : ''
    const checkout = dateRange[0].endDate ? format(dateRange[0].endDate, 'yyyy-MM-dd') : ''
    const { language, currency } = getSiteSettings()
    const searchBasePath = '/hotels'
    
    const adultsTotal = rooms.reduce((s, r) => s + (Number(r.adults) || 0), 0) || 1
    const roomsCount = rooms.length || 1

    // Children ages → comma-separated `children` param
    const allChildrenAges = rooms.reduce((acc, r) => {
      const ages = (r.childAges || []).filter(a => a !== null && typeof a !== 'undefined')
      return acc.concat(ages)
    }, [])
    const childrenParam = allChildrenAges.length ? allChildrenAges.join(',') : ''
    if (isHotelOrLodging(selectedPlace)) {
      console.log('[HomePage] Hotel/lodging detected, fetching place details for coords')
      const placeDetails = await fetchPlaceDetails(placeId)
      
      if (placeDetails && placeDetails.location) {
        const { latitude, longitude } = placeDetails.location
        const hotelName = placeDetails.displayName || selectedPlace?.displayName || selectedPlace?.name || destination || ''
        
        const params = new URLSearchParams()
        // Use coords as placeId for hotel search (like mobile app)
        params.set('placeId', `${latitude},${longitude}`)
        params.set('placeTypes', 'lodging')
        params.set('name', hotelName)
        if (checkin) params.set('checkin', checkin)
        if (checkout) params.set('checkout', checkout)
        params.set('rooms', String(roomsCount))
        params.set('adults', String(adultsTotal))
        if (childrenParam) params.set('children', childrenParam)
        params.set('sorting', '1')
        params.set('searchHotelName', hotelName)
        params.set('matchHotelName', hotelName) // For fuzzy matching after results load
        params.set('language', language)
        params.set('currency', currency)
        if (searchType === 'rentals') params.set('vacationRentals', 'true')

        console.log(`[HomePage] Hotel search navigate to ${searchBasePath}`)
        navigate(`${searchBasePath}?${params.toString()}`)
        return
      } else {
        console.warn('[HomePage] Could not get coords for hotel, falling back to regular search')
      }
    }

    // Regular place search (city, airport, etc.)
    const placeName = selectedPlace?.displayName || selectedPlace?.name || destination || ''
    const formattedAddress = selectedPlace?.formattedAddress || ''
    const params = new URLSearchParams()
    if (placeId) params.set('placeId', placeId)
    params.set('placeTypes', 'geocode')
    if (placeName) params.set('name', placeName)
    if (formattedAddress) {
      params.set('location', formattedAddress)
      const countryPart = formattedAddress.split(',').map(s => s.trim()).filter(Boolean).at(-1)
      if (countryPart) params.set('country', countryPart)
    }
    if (checkin) params.set('checkin', checkin)
    if (checkout) params.set('checkout', checkout)
    params.set('rooms', String(roomsCount))
    params.set('adults', String(adultsTotal))
    if (childrenParam) params.set('children', childrenParam)
    params.set('sorting', '1')
    params.set('language', language)
    params.set('currency', currency)
    if (searchType === 'rentals') params.set('vacationRentals', 'true')

    console.log(`[HomePage] Regular search navigate to ${searchBasePath}`)
    navigate(`${searchBasePath}?${params.toString()}`)
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className={`hero ${searchType === 'flights' ? 'hero-flights' : ''}`}>
        <div className="hero-content">
          <h1>Find your perfect stay</h1>
          <p>Stay anywhere. Do everything. Book hotels, vacation rentals, experiences, and flights.</p>

          {/* Desktop Search Bar */}
          <div className={`search-bar desktop-search ${searchType === 'flights' ? 'flights-mode' : ''}`}>
            {/* Property Type Toggle - Desktop */}
            <div className="property-type-toggle desktop-only">
              <button 
                className={`property-type-btn ${searchType === 'hotels' ? 'active' : ''}`}
                onClick={() => setSearchType('hotels')}
              >
                <HiOutlineBuildingOffice2 size={18} />
                Hotels
              </button>
              {FLIGHTS_SEARCH_ENABLED && (
                <button
                  className={`property-type-btn ${searchType === 'flights' ? 'active' : ''}`}
                  onClick={() => setSearchType('flights')}
                >
                  <IoAirplaneOutline size={18} />
                  Flights
                </button>
              )}
              <button 
                className={`property-type-btn ${searchType === 'rentals' ? 'active' : ''}`}
                onClick={() => setSearchType('rentals')}
              >
                <HiOutlineHomeModern size={18} />
                Vacation Rentals
              </button>
              <button 
                className={`property-type-btn ${searchType === 'experiences' ? 'active' : ''}`}
                onClick={() => setSearchType('experiences')}
              >
                <FaHiking size={18} />
                Experiences
              </button>
            </div>

            {/* Flights: Trip type segmented control */}
            {searchType === 'flights' && (
              <div className="flight-trip-type-row" role="radiogroup" aria-label="Trip type">
                {[['roundtrip', 'Round-trip'], ['oneway', 'One-way']].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`trip-type-option ${flightTripType === val ? 'active' : ''}`}
                    aria-pressed={flightTripType === val}
                    onClick={() => setFlightTripType(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Flights: From / To fields with airport autocomplete */}
            {searchType === 'flights' && <>
              <div className="search-field flight-field flight-airport-field flight-from-field" ref={flightFromRef}>
                <span className="flight-field-icon field-leading-icon-origin"><IoAirplaneOutline size={18} /></span>
                <label>From</label>
                <input
                  type="text"
                  placeholder="Airport or city"
                  value={fromAirport.query}
                  onChange={(e) => fromAirport.setQuery(e.target.value)}
                  onFocus={() => fromAirport.focusForEdit()}
                  className={fromAirport.query ? 'has-value' : ''}
                />
                {(fromAirport.showSuggestions || fromAirport.loading) && (
                  <div className="dropdown suggestions-dropdown airport-dropdown">
                    {fromAirport.loading
                      ? <div className="dropdown-loading">Searching airports...</div>
                      : fromAirport.suggestions.map((ap) => (
                        <div key={`${ap.iata}-${ap.icao || 'all'}`} className="dropdown-item airport-item" onClick={() => fromAirport.handleSelect(ap)}>
                          <div className="airport-iata-badge">{ap.iata}</div>
                          <div className="place-info">
                            <span className="place-name">{flightAirportName(ap)}</span>
                            <span className={`airport-code-type ${isFlightCityCode(ap) ? 'city' : 'airport'}`}>
                              {isFlightCityCode(ap) ? 'City search' : 'Exact airport'}
                            </span>
                            <span className="place-address">{ap.city}{ap.state ? `, ${ap.state}` : ''} · {ap.country}</span>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              <div className="search-divider" />
              <div className="search-field flight-field flight-airport-field flight-to-field" ref={flightToRef}>
                <span className="flight-field-icon field-leading-icon-destination"><FiMapPin size={18} /></span>
                <label>To</label>
                <input
                  type="text"
                  placeholder="Airport or city"
                  value={toAirport.query}
                  onChange={(e) => toAirport.setQuery(e.target.value)}
                  onFocus={() => toAirport.focusForEdit()}
                  className={toAirport.query ? 'has-value' : ''}
                />
                {(toAirport.showSuggestions || toAirport.loading) && (
                  <div className="dropdown suggestions-dropdown airport-dropdown">
                    {toAirport.loading
                      ? <div className="dropdown-loading">Searching airports...</div>
                      : toAirport.suggestions.map((ap) => (
                        <div key={`${ap.iata}-${ap.icao || 'all'}`} className="dropdown-item airport-item" onClick={() => toAirport.handleSelect(ap)}>
                          <div className="airport-iata-badge">{ap.iata}</div>
                          <div className="place-info">
                            <span className="place-name">{flightAirportName(ap)}</span>
                            <span className={`airport-code-type ${isFlightCityCode(ap) ? 'city' : 'airport'}`}>
                              {isFlightCityCode(ap) ? 'City search' : 'Exact airport'}
                            </span>
                            <span className="place-address">{ap.city}{ap.state ? `, ${ap.state}` : ''} · {ap.country}</span>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            </>}

            {/* Hotels/Rentals/Experiences: Where field */}
            {searchType !== 'flights' && <div className="search-field where-field" ref={destinationRef}>
              <label>Where</label>
              <input
                type="text"
                placeholder="Search destinations"
                value={destination}
                onChange={handleDestinationChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                className={destination ? 'has-value' : ''}
              />
              {showSuggestions && (suggestions.length > 0 || loadingSuggestions) && (
                <div className="dropdown suggestions-dropdown">
                  {loadingSuggestions ? (
                    <div className="dropdown-loading">Searching...</div>
                  ) : suggestions.map((place, i) => (
                    <div key={place.viatorId || place.placeId || i} className="dropdown-item" onClick={() => handleSelectPlace(place)}>
                      <div className="place-icon">
                        {place.isViator ? <FiGlobe size={20} /> :
                         place.types?.some(t => t?.toLowerCase().includes('hotel')) ? <HiOutlineBuildingOffice2 size={20} /> :
                         place.types?.some(t => t?.toLowerCase().includes('airport')) ? <IoAirplaneOutline size={20} /> : <FiMapPin size={20} />}
                      </div>
                      <div className="place-info">
                        <span className="place-name">{truncateText(place.displayName || place.name, 45)}</span>
                        <span className="place-address">{place.formattedAddress || ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>}

            {(searchType === 'hotels' || searchType === 'rentals' || searchType === 'flights') && <div className="search-divider" />}

            {/* Dates */}
            {(searchType === 'hotels' || searchType === 'rentals' || searchType === 'flights') && <div className="search-field dates-field" ref={dateRef}>
              {searchType === 'flights' && <span className="flight-field-icon"><FiCalendar size={18} /></span>}
              <label>Dates</label>
              <button className="field-button" onClick={() => setShowDatePicker(!showDatePicker)}>
                <span>{formatDateDisplay()}</span>
              </button>
              {showDatePicker && (
                <div className={`dropdown date-dropdown desktop-calendar-dropdown ${!dateRange[0].startDate ? 'no-selection' : ''}`}>
                  <div className="date-dropdown-header">
                    <span className="date-dropdown-range">{formatDateDisplay()}</span>
                    {getNightsCount() > 0 && (
                      <span className="date-dropdown-nights">{getNightsCount()} Night{getNightsCount() > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="desktop-calendar-wrapper">
                    <CalendarPicker
                      startDate={dateRange[0].startDate}
                      endDate={dateRange[0].endDate}
                      onDateChange={(start, end) => setDateRange([{ startDate: start, endDate: end, key: 'selection' }])}
                      mode="side-by-side"
                      onClose={() => setShowDatePicker(false)}
                      singleDate={searchType === 'flights' && flightTripType === 'oneway'}
                    />
                  </div>
                </div>
              )}
            </div>}

            {(searchType === 'hotels' || searchType === 'rentals' || searchType === 'flights') && <div className="search-divider" />}

            {/* Guests (Hotels/Rentals) */}
            {(searchType === 'hotels' || searchType === 'rentals') && <div className="search-field guests-field" ref={guestRef}>
              <label>Guests</label>
              <button className="field-button" onClick={() => setShowGuestPicker(!showGuestPicker)}>
                {guestSummary()}
              </button>
              {showGuestPicker && (
                <div className="dropdown guest-dropdown">
                  <div className="room-block">
                    <div className="guest-row">
                      <span>Adults</span>
                      <div className="stepper">
                        <button onClick={() => updateRoom(0, 'adults', Math.max(1, rooms[0].adults - 1))} disabled={rooms[0].adults <= 1}>−</button>
                        <span>{rooms[0].adults}</span>
                        <button onClick={() => updateRoom(0, 'adults', Math.min(8, rooms[0].adults + 1))} disabled={rooms[0].adults >= 8}>+</button>
                      </div>
                    </div>
                    <div className="guest-row">
                      <span>Children</span>
                      <div className="stepper">
                        <button onClick={() => updateRoom(0, 'children', Math.max(0, rooms[0].children - 1))} disabled={rooms[0].children <= 0}>−</button>
                        <span>{rooms[0].children}</span>
                        <button onClick={() => updateRoom(0, 'children', Math.min(6, rooms[0].children + 1))} disabled={rooms[0].children >= 6}>+</button>
                      </div>
                    </div>
                    {rooms[0].children > 0 && (
                      <div className="child-ages">
                        {rooms[0].childAges.map((age, ci) => (
                          <div key={ci} className="child-age-row">
                            <span>Child {ci + 1} age</span>
                            <select value={age} onChange={(e) => updateChildAge(0, ci, Number(e.target.value))}>
                              <option value={0}>Under 1</option>
                              {[...Array(17)].map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="dropdown-done" onClick={() => setShowGuestPicker(false)}>Done</button>
                </div>
              )}
            </div>}

            {/* Travelers (Flights) */}
            {searchType === 'flights' && <div className="search-field guests-field" ref={travelerRef}>
              <span className="flight-field-icon"><HiOutlineUsers size={18} /></span>
              <label>Travelers</label>
              <button className="field-button" onClick={() => setShowTravelerPicker(!showTravelerPicker)}>
                <span>{flightTravelerSummary()}</span>
              </button>
              {showTravelerPicker && (
                <div className="dropdown traveler-dropdown">
                  <div className="guest-row">
                    <div className="guest-row-label">
                      <span>Adults</span>
                      <span className="guest-row-sub">Age 12+</span>
                    </div>
                    <div className="stepper">
                      <button onClick={() => setFlightAdults(Math.max(1, flightAdults - 1))} disabled={flightAdults <= 1}>−</button>
                      <span>{flightAdults}</span>
                      <button onClick={() => setFlightAdults(Math.min(9, flightAdults + 1))} disabled={flightAdults >= 9}>+</button>
                    </div>
                  </div>
                  <div className="guest-row">
                    <div className="guest-row-label">
                      <span>Children</span>
                      <span className="guest-row-sub">Ages 2–11</span>
                    </div>
                    <div className="stepper">
                      <button onClick={removeFlightChild} disabled={flightChildren <= 0}>−</button>
                      <span>{flightChildren}</span>
                      <button onClick={addFlightChild} disabled={flightChildren >= 8}>+</button>
                    </div>
                  </div>
                  {flightChildAges.length > 0 && (
                    <div className="child-ages">
                      {flightChildAges.map((age, idx) => (
                        <div key={idx} className="child-age-row">
                          <span>Child {idx + 1} age</span>
                          <select value={age} onChange={(e) => setFlightChildAge(idx, Number(e.target.value))}>
                            {Array.from({ length: 10 }, (_, i) => i + 2).map(a => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="guest-row">
                    <div className="guest-row-label">
                      <span>Infants</span>
                      <span className="guest-row-sub">Under 2</span>
                    </div>
                    <div className="stepper">
                      <button onClick={() => setFlightInfants(Math.max(0, flightInfants - 1))} disabled={flightInfants <= 0}>−</button>
                      <span>{flightInfants}</span>
                      <button onClick={() => setFlightInfants(Math.min(flightAdults, flightInfants + 1))} disabled={flightInfants >= flightAdults}>+</button>
                    </div>
                  </div>
                  <button className="dropdown-done" onClick={() => setShowTravelerPicker(false)}>Done</button>
                </div>
              )}
            </div>}

            {/* Cabin Class (Flights) */}
            {searchType === 'flights' && <>
              <div className="search-divider" />
              <div className="search-field cabin-field" ref={cabinRef}>
                <span className="flight-field-icon"><IoAirplaneOutline size={18} /></span>
                <label>Cabin Class</label>
                <div className="cabin-select-shell">
                  <button
                    type="button"
                    className="cabin-select"
                    onClick={() => setShowCabinPicker(prev => !prev)}
                  >
                    <span>{flightCabinLabel}</span>
                    <FiChevronRight className="select-chevron" size={16} />
                  </button>
                  {showCabinPicker && (
                    <div className="cabin-dropdown">
                      {CABIN_CLASS_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          className={`cabin-option ${flightCabinClass === option.value ? 'active' : ''}`}
                          onClick={() => {
                            setFlightCabinClass(option.value)
                            setShowCabinPicker(false)
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>}

            {/* Search Button (hidden in flights mode) */}
            {searchType !== 'flights' && (
              <button className="search-btn" onClick={handleSearch}>
                <FiSearch size={20} strokeWidth={2.5} />
              </button>
            )}

            {/* Flights: Find Your Flight button */}
            {searchType === 'flights' && (
              <div className="flights-bottom-row">
                <button className="flight-find-btn" onClick={handleSearch}>
                  <FiSearch size={18} strokeWidth={2.5} />
                  Find Your Flight
                </button>
              </div>
            )}
          </div>

          {/* Mobile Search Card */}
          {/* Mobile Property Type Toggle */}
          <div className="property-type-toggle mobile-property-toggle">
            <button 
              className={`property-type-btn ${searchType === 'hotels' ? 'active' : ''}`}
              onClick={() => setSearchType('hotels')}
            >
              <HiOutlineBuildingOffice2 size={16} />
              Hotels
            </button>
            {FLIGHTS_SEARCH_ENABLED && (
              <button
                className={`property-type-btn ${searchType === 'flights' ? 'active' : ''}`}
                onClick={() => setSearchType('flights')}
              >
                <IoAirplaneOutline size={16} />
                Flights
              </button>
            )}
            <button 
              className={`property-type-btn ${searchType === 'rentals' ? 'active' : ''}`}
              onClick={() => setSearchType('rentals')}
            >
              <HiOutlineHomeModern size={16} />
              Rentals
            </button>
            <button 
              className={`property-type-btn ${searchType === 'experiences' ? 'active' : ''}`}
              onClick={() => setSearchType('experiences')}
            >
              <FaHiking size={16} />
              Experiences
            </button>
          </div>

          <div className="mobile-search-card">
            {/* Flights: Trip type segmented control */}
            {searchType === 'flights' && (
              <div className="mobile-trip-type-row">
                {[['roundtrip', 'Round-trip'], ['oneway', 'One-way']].map(([val, label]) => (
                  <button
                    key={val}
                    className={`mobile-trip-type-btn ${flightTripType === val ? 'active' : ''}`}
                    onClick={() => setFlightTripType(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Flights: From / To Rows — tap to open airport modal */}
            {searchType === 'flights' && <>
              <button className="mobile-search-row" onClick={() => setAirportModalField('from')}>
                <IoAirplaneOutline size={20} className="row-icon" />
                <div className="row-content">
                  <span className="row-label">From</span>
                  <span className={`row-value ${!fromAirport.query ? 'placeholder' : ''}`}>
                    {fromAirport.query || 'Leaving from'}
                  </span>
                </div>
                <FiChevronRight size={20} className="row-arrow" />
              </button>
              <button className="mobile-search-row" onClick={() => setAirportModalField('to')}>
                <FiMapPin size={20} className="row-icon" />
                <div className="row-content">
                  <span className="row-label">To</span>
                  <span className={`row-value ${!toAirport.query ? 'placeholder' : ''}`}>
                    {toAirport.query || 'Going to'}
                  </span>
                </div>
                <FiChevronRight size={20} className="row-arrow" />
              </button>
            </>}

            {/* Hotels/Rentals/Experiences: Destination Row */}
            {searchType !== 'flights' && <button className="mobile-search-row" onClick={() => { setMobileSearchQuery(destination); setShowDestinationModal(true); }}>
              <FiMapPin size={20} className="row-icon" />
              <div className="row-content">
                <span className="row-label">Destination</span>
                <span className={`row-value ${!destination ? 'placeholder' : ''}`}>
                  {destination || 'Where are you going?'}
                </span>
              </div>
              <FiChevronRight size={20} className="row-arrow" />
            </button>}

            {/* Dates Row */}
            {(searchType === 'hotels' || searchType === 'rentals' || searchType === 'flights') && <button className="mobile-search-row" onClick={() => setShowDateModal(true)}>
              <FiCalendar size={20} className="row-icon" />
              <div className="row-content">
                <span className="row-label">Dates</span>
                <span className={`row-value ${!dateRange[0].startDate ? 'placeholder' : ''}`}>
                  {searchType === 'flights' && flightTripType === 'oneway'
                    ? (dateRange[0].startDate ? formatDateDisplay() : 'Select date')
                    : (dateRange[0].startDate && dateRange[0].endDate
                        ? `${formatDateDisplay()}${getNightsCount() > 0 ? ` · ${getNightsCount()} night${getNightsCount() > 1 ? 's' : ''}` : ''}`
                        : 'Select dates')}
                </span>
              </div>
              <FiChevronRight size={20} className="row-arrow" />
            </button>}

            {/* Travelers Row (Hotels/Rentals) */}
            {(searchType === 'hotels' || searchType === 'rentals') && <button className="mobile-search-row" onClick={() => setShowGuestModal(true)}>
              <HiOutlineUsers size={20} className="row-icon" />
              <div className="row-content">
                <span className="row-label">Travelers</span>
                <span className="row-value">{guestSummary()}</span>
              </div>
              <FiChevronRight size={20} className="row-arrow" />
            </button>}

            {/* Travelers Row (Flights) */}
            {searchType === 'flights' && (
              <div className="mobile-flight-travelers-picker">
                <button
                  type="button"
                  className="mobile-search-row"
                  onClick={() => setShowTravelerPicker(prev => !prev)}
                >
                  <HiOutlineUsers size={20} className="row-icon" />
                  <div className="row-content">
                    <span className="row-label">Travelers</span>
                    <span className="row-value">{flightTravelerSummary()}</span>
                  </div>
                  <FiChevronRight size={20} className="row-arrow row-arrow-down" />
                </button>
                {showTravelerPicker && (
                  <div className="mobile-flight-dropdown mobile-traveler-dropdown">
                    <div className="mobile-traveler-row">
                      <div className="mobile-traveler-label">
                        <span>Adults</span>
                        <span className="mobile-traveler-sub">Age 12+</span>
                      </div>
                      <div className="mobile-stepper">
                        <button className="stepper-btn" onClick={() => setFlightAdults(Math.max(1, flightAdults - 1))} disabled={flightAdults <= 1}>−</button>
                        <span className="stepper-value">{flightAdults}</span>
                        <button className="stepper-btn" onClick={() => setFlightAdults(Math.min(9, flightAdults + 1))} disabled={flightAdults >= 9}>+</button>
                      </div>
                    </div>
                    <div className="mobile-traveler-row">
                      <div className="mobile-traveler-label">
                        <span>Children</span>
                        <span className="mobile-traveler-sub">Ages 2–11</span>
                      </div>
                      <div className="mobile-stepper">
                        <button className="stepper-btn" onClick={removeFlightChild} disabled={flightChildren <= 0}>−</button>
                        <span className="stepper-value">{flightChildren}</span>
                        <button className="stepper-btn" onClick={addFlightChild} disabled={flightChildren >= 8}>+</button>
                      </div>
                    </div>
                    {flightChildAges.length > 0 && (
                      <div className="mobile-child-ages">
                        {flightChildAges.map((age, idx) => (
                          <div key={idx} className="mobile-child-age-row">
                            <span>Child {idx + 1} age</span>
                            <select value={age} onChange={(e) => setFlightChildAge(idx, Number(e.target.value))}>
                              {Array.from({ length: 10 }, (_, i) => i + 2).map(a => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mobile-traveler-row">
                      <div className="mobile-traveler-label">
                        <span>Infants</span>
                        <span className="mobile-traveler-sub">Under 2</span>
                      </div>
                      <div className="mobile-stepper">
                        <button className="stepper-btn" onClick={() => setFlightInfants(Math.max(0, flightInfants - 1))} disabled={flightInfants <= 0}>−</button>
                        <span className="stepper-value">{flightInfants}</span>
                        <button className="stepper-btn" onClick={() => setFlightInfants(Math.min(flightAdults, flightInfants + 1))} disabled={flightInfants >= flightAdults}>+</button>
                      </div>
                    </div>
                    <button className="mobile-dropdown-done" onClick={() => setShowTravelerPicker(false)}>Done</button>
                  </div>
                )}
              </div>
            )}

            {/* Cabin Class Row (Flights) */}
            {searchType === 'flights' && (
              <div className="mobile-flight-cabin-picker">
                <button
                  type="button"
                  className="mobile-search-row mobile-cabin-row"
                  onClick={() => setShowCabinPicker(prev => !prev)}
                >
                  <IoAirplaneOutline size={20} className="row-icon" />
                  <div className="row-content">
                    <span className="row-label">Cabin Class</span>
                    <span className="row-value">{flightCabinLabel}</span>
                  </div>
                  <FiChevronRight size={20} className="row-arrow row-arrow-down" />
                </button>
                {showCabinPicker && (
                  <div className="mobile-flight-dropdown mobile-cabin-dropdown">
                    {CABIN_CLASS_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`mobile-cabin-option ${flightCabinClass === option.value ? 'active' : ''}`}
                        onClick={() => {
                          setFlightCabinClass(option.value)
                          setShowCabinPicker(false)
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Search Button */}
            <button className={`mobile-search-btn ${searchType === 'flights' ? 'mobile-flight-btn' : ''}`} onClick={handleSearch}>
              <FiSearch size={20} />
              {searchType === 'flights' ? 'Find Your Flight' : 'Search'}
            </button>
          </div>
        </div>
      </section>

      {/* Mobile Airport Search Modal */}
      {airportModalField && (
        <div className="mobile-modal-overlay">
          <div className="mobile-modal">
            <div className="mobile-modal-header destination-modal-header-light">
              <button className="modal-close-btn-light" onClick={() => setAirportModalField(null)}>
                <FiX size={22} />
              </button>
              <h3 className="modal-title-light">
                {airportModalField === 'from' ? 'Leaving from' : 'Going to'}
              </h3>
              <div className="modal-header-spacer" />
            </div>
            <div className="mobile-modal-content">
              <div className="mobile-search-input-wrapper">
                <FiSearch size={20} className="search-input-icon" />
                <input
                  ref={airportModalInputRef}
                  type="text"
                  placeholder="Search airports or cities..."
                  value={activeAirport.query}
                  onChange={(e) => activeAirport.setQuery(e.target.value)}
                  className="mobile-search-input"
                />
                {activeAirport.query && (
                  <button className="clear-input-btn" onClick={() => activeAirport.clear()}>
                    <FiX size={18} />
                  </button>
                )}
              </div>
              <div className="mobile-suggestions-list">
                {activeAirport.loading && (
                  <div className="mobile-suggestion-loading">Searching airports...</div>
                )}
                {activeAirport.suggestions.map((ap) => (
                  <button
                    key={`${ap.iata}-${ap.icao || 'all'}`}
                    className="mobile-suggestion-item"
                    onClick={() => { activeAirport.handleSelect(ap); setAirportModalField(null) }}
                  >
                    <div className="suggestion-icon airport-iata-badge">{ap.iata}</div>
                    <div className="suggestion-info">
                      <span className="suggestion-name">{flightAirportName(ap)}</span>
                      <span className={`airport-code-type ${isFlightCityCode(ap) ? 'city' : 'airport'}`}>
                        {isFlightCityCode(ap) ? 'City search' : 'Exact airport'}
                      </span>
                      <span className="suggestion-address">{ap.city}{ap.state ? `, ${ap.state}` : ''} · {ap.country}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Destination Modal */}
      {showDestinationModal && (
        <div className="mobile-modal-overlay">
          <div className="mobile-modal">
            <div className="mobile-modal-header destination-modal-header-light">
              <button className="modal-close-btn-light" onClick={() => setShowDestinationModal(false)}>
                <FiX size={22} />
              </button>
              <h3 className="modal-title-light">Destination</h3>
              <div className="modal-header-spacer" />
            </div>
            <div className="mobile-modal-content">
              <div className="mobile-search-input-wrapper">
                <FiSearch size={20} className="search-input-icon" />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search cities, hotels, airports..."
                  value={mobileSearchQuery}
                  onChange={handleMobileSearchChange}
                  className="mobile-search-input"
                />
                {mobileSearchQuery && (
                  <button className="clear-input-btn" onClick={() => { setMobileSearchQuery(''); setSuggestions([]); }}>
                    <FiX size={18} />
                  </button>
                )}
              </div>
              <div className="mobile-suggestions-list">
                {loadingSuggestions && (
                  <div className="mobile-suggestion-loading">Searching...</div>
                )}
                {suggestions.map((place, i) => (
                  <button 
                    key={place.viatorId || place.placeId || i} 
                    className="mobile-suggestion-item"
                    onClick={() => handleSelectPlace(place)}
                  >
                    <div className="suggestion-icon">
                      {place.isViator ? <FiGlobe size={24} /> :
                       place.types?.some(t => t?.toLowerCase().includes('hotel')) ? <HiOutlineBuildingOffice2 size={24} /> :
                       place.types?.some(t => t?.toLowerCase().includes('airport')) ? <IoAirplaneOutline size={24} /> : <FiMapPin size={24} />}
                    </div>
                    <div className="suggestion-info">
                      <span className="suggestion-name">{truncateText(place.displayName || place.name, 40)}</span>
                      <span className="suggestion-address">{place.formattedAddress || ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Date Modal */}
      {showDateModal && (
        <div className="mobile-modal-overlay">
          <div className="mobile-modal">
            <div className="mobile-modal-header date-modal-header-light">
              <button className="modal-close-btn-light" onClick={() => setShowDateModal(false)}>
                <FiX size={22} />
              </button>
              <div className="date-header-text-light">
                {searchType === 'flights' && flightTripType === 'oneway' ? (
                  <span className="date-range-display-light">
                    {dateRange[0].startDate
                      ? dateRange[0].startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      : 'Select date'}
                  </span>
                ) : (
                  <>
                    <span className="date-range-display-light">
                      {dateRange[0].startDate && dateRange[0].endDate ? formatDateDisplay() : 'Select dates'}
                    </span>
                    {dateRange[0].startDate && dateRange[0].endDate && getNightsCount() > 0 && (
                      <span className="nights-count-light">
                        {getNightsCount()} Night{getNightsCount() > 1 ? 's' : ''}
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="modal-header-spacer" />
            </div>
            <div className="mobile-modal-content date-modal-content">
              <CalendarPicker
                startDate={dateRange[0].startDate}
                endDate={dateRange[0].endDate}
                onDateChange={(start, end) => setDateRange([{ startDate: start, endDate: end, key: 'selection' }])}
                singleDate={searchType === 'flights' && flightTripType === 'oneway'}
              />
            </div>
            <div className="mobile-modal-footer">
              <button className="modal-done-btn" onClick={() => setShowDateModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Guest Modal */}
      {showGuestModal && (
        <div className="mobile-modal-overlay">
          <div className="mobile-modal">
            <div className="mobile-modal-header guest-modal-header-light">
              <button className="modal-close-btn-light" onClick={() => setShowGuestModal(false)}>
                <FiX size={22} />
              </button>
              <h3 className="guest-modal-title">Travelers</h3>
              <div className="modal-header-spacer" />
            </div>
            <div className="mobile-modal-content guest-modal-content">
              <div className="mobile-room-block">
                <div className="mobile-guest-row">
                  <div className="guest-type">
                    <HiOutlineUsers size={20} />
                    <span>Adults</span>
                  </div>
                  <div className="mobile-stepper">
                    <button
                      className="stepper-btn"
                      onClick={() => updateRoom(0, 'adults', Math.max(1, rooms[0].adults - 1))}
                      disabled={rooms[0].adults <= 1}
                    >−</button>
                    <span className="stepper-value">{rooms[0].adults}</span>
                    <button
                      className="stepper-btn"
                      onClick={() => updateRoom(0, 'adults', Math.min(8, rooms[0].adults + 1))}
                      disabled={rooms[0].adults >= 8}
                    >+</button>
                  </div>
                </div>

                <div className="mobile-guest-row">
                  <div className="guest-type">
                    <HiOutlineUsers size={18} />
                    <span>Children</span>
                  </div>
                  <div className="mobile-stepper">
                    <button
                      className="stepper-btn"
                      onClick={() => updateRoom(0, 'children', Math.max(0, rooms[0].children - 1))}
                      disabled={rooms[0].children <= 0}
                    >−</button>
                    <span className="stepper-value">{rooms[0].children}</span>
                    <button
                      className="stepper-btn"
                      onClick={() => updateRoom(0, 'children', Math.min(6, rooms[0].children + 1))}
                      disabled={rooms[0].children >= 6}
                    >+</button>
                  </div>
                </div>

                {rooms[0].children > 0 && (
                  <div className="mobile-child-ages">
                    {rooms[0].childAges.map((age, ci) => (
                      <div key={ci} className="mobile-child-age-row">
                        <span>Child {ci + 1} age</span>
                        <select value={age} onChange={(e) => updateChildAge(0, ci, Number(e.target.value))}>
                          <option value={0}>Under 1</option>
                          {[...Array(17)].map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="mobile-modal-footer">
              <button className="modal-done-btn" onClick={() => setShowGuestModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Regional Deals Section */}
      <RegionalDeals />

      {/* Voyabay Card Promo */}
      <section className="card-promo">
        <div className="container">
          <div className="card-promo-card">
            <div className="card-promo-image">
              <img src="/voyabaycard.png" alt="Voyabay Card" />
            </div>
            <div className="card-promo-content">
              <h2>
                <span className="card-promo-crossed">Annual Fees</span>{' '}
                <span className="card-promo-highlight">Annual Bonus</span>
              </h2>
              <ul className="card-promo-perks">
                <li>Earn 1.5% back on all purchases</li>
                <li>No credit check</li>
              </ul>
              <a href="/card" className="card-promo-cta">
                Join waiting list
                <FiArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <PopularDestinations />

      {/* Experiences Promo */}
      <section className="experiences-promo">
        <div className="container">
          <div className="experiences-card">
            <div className="experiences-video">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
              >
                <source src="https://firebasestorage.googleapis.com/v0/b/voyabay-hotels.firebasestorage.app/o/Untitled%20(12).mp4?alt=media&token=14f54bab-fca3-437d-bbfc-662873e507e0" type="video/mp4" />
              </video>
            </div>
            <div className="experiences-content">
              <h2>Discover unforgettable experiences</h2>
              <p className="experiences-description">
                From walking tours to adventure activities, find and book incredible experiences powered by Viator, the world's largest travel experiences marketplace.
              </p>
              <a href="https://experiences.voyabay.com" target="_blank" rel="noopener noreferrer" className="experiences-cta">
                Explore experiences
                <FiArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Marriott Hawaii */}
      <FeaturedMarriottHawaii />

      {/* Buy Now Pay Later Promo */}
      <section className="bnpl-promo">
        <div className="container">
          <div className="bnpl-card">
            <div className="bnpl-image">
              <img src="/bnpl-optimized.webp" alt="Book now, pay later" width="533" height="300" />
            </div>
            <div className="bnpl-content">
              <div className="bnpl-logos">
                <img src="/klarna-optimized.webp" alt="Klarna" className="bnpl-logo" width="120" height="70" />
              </div>
              <h2>Book now, pay later</h2>
              <p className="bnpl-description">Split your stay into 4 interest-free payments.</p>
              <p className="bnpl-subtitle">No hidden fees. Instant approval.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="container">
          <div className="features-card">
            <div className="features-grid">
              <div className="feature">
                <img src="/stripe-optimized.webp" alt="Stripe" className="feature-logo" width="144" height="60" />
                <h3>Secure Payments</h3>
                <p>Your payments are protected with industry-leading encryption by Stripe.</p>
              </div>
              <div className="feature">
                <img src="/nohiddenfees-optimized.webp" alt="No Hidden Fees" className="feature-logo" width="60" height="60" />
                <h3>No Hidden Fees</h3>
                <p>The price you see is the price you pay. No surprise charges at checkout.</p>
              </div>
              <div className="feature">
                <img src="/viator-optimized.webp" alt="Viator" className="feature-logo" width="238" height="60" />
                <h3>Experiences</h3>
                <p>Discover tours and activities powered by Viator, the world's largest experiences marketplace.</p>
              </div>
              <div className="feature">
                <img src="/support-optimized.webp" alt="24/7 Support" className="feature-logo" width="60" height="60" />
                <h3>24/7 Support</h3>
                <p>Our customer service team is available around the clock to assist you.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <h2>Ready to book your next adventure?</h2>
          <p>Create a free account and start exploring today.</p>
          {!user && (
            <div className="cta-buttons">
              <Link to="/signup" className="btn-primary">Create Account</Link>
              <Link to="/login" className="btn-secondary">Sign In</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default HomePage
